import { PlantData } from './plants-master-database';

export interface CuratedCollection {
  id: string;
  name: string;
  emoji: string;
  description: string;
  forExperience: 'novice' | 'intermediate' | 'expert' | 'all';
  forInterests: string[];
  forMethods: string[];
  forSpaces: string[];
  plantCount: number;
  tags: string[];
  plantIds?: string[];
  filterFn?: (plant: PlantData) => boolean;
}

// Beginner's Dozen - 12 easiest plants for first-time gardeners
export const beginnersDozen: CuratedCollection = {
  id: 'beginners-dozen',
  name: "Beginner's Dozen",
  emoji: '🌱',
  description: '12 foolproof plants perfect for your first garden. These are forgiving, productive, and delicious!',
  forExperience: 'novice',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['easy', 'fast-growing', 'productive', 'forgiving'],
  plantIds: [
    'tomato-cherry',           // Cherry tomatoes - prolific and easy
    'lettuce-leaf',            // Leaf lettuce - grows fast
    'basil-sweet',             // Basil - aromatic, easy herb
    'pepper-bell',             // Bell peppers - reliable
    'cucumber-slicing',        // Cucumbers - productive vines
    'carrot-nantes',           // Nantes carrots - sweet, easy
    'radish-cherry-belle',     // Cherry Belle radish - 30 days!
    'bean-bush-blue-lake',     // Bush beans - no trellis needed
    'zucchini-black-beauty',   // Zucchini - incredibly productive
    'spinach-bloomsdale',      // Spinach - cool weather, fast
    'parsley-italian-flat',    // Parsley - versatile herb
    'chives-common',           // Chives - perennial, easy
  ],
};

// Container Garden Collection
export const containerGarden: CuratedCollection = {
  id: 'container-garden',
  name: 'Container Garden',
  emoji: '🪴',
  description: 'Perfect for balconies, patios, and small spaces. Compact varieties that thrive in pots.',
  forExperience: 'all',
  forInterests: ['vegetables', 'herbs', 'flowers'],
  forMethods: ['containers'],
  forSpaces: ['small'],
  plantCount: 25,
  tags: ['compact', 'patio', 'balcony', 'pots'],
  filterFn: (plant) => {
    // Compact/dwarf varieties, herbs, and plants that do well in containers
    const compactKeywords = ['dwarf', 'compact', 'patio', 'container', 'bush', 'mini', 'tiny'];
    const name = plant.name.toLowerCase();
    const isCompact = compactKeywords.some(kw => name.includes(kw));
    const isHerb = plant.category === 'herb';
    const smallSpacing = plant.spacing <= 12;
    return isCompact || isHerb || smallSpacing;
  },
};

// Salsa Garden
export const salsaGarden: CuratedCollection = {
  id: 'salsa-garden',
  name: 'Salsa Garden',
  emoji: '🌶️',
  description: 'Everything you need for fresh homemade salsa! Tomatoes, peppers, cilantro, onions, and more.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 8,
  tags: ['mexican', 'cooking', 'fresh', 'spicy'],
  plantIds: [
    'tomato-roma',             // Paste tomatoes for salsa
    'tomato-cherry',           // Fresh eating tomatoes
    'pepper-jalapeno',         // Classic salsa heat
    'pepper-bell',             // Sweet crunch
    'cilantro-slow-bolt',      // Fresh herb (slow to bolt)
    'onion-yellow-spanish',    // Yellow onions
    'garlic-hardneck',         // Garlic for depth
    'lime-tree-dwarf',         // Fresh lime (if space allows)
  ],
};

// Salad Bowl Collection
export const saladBowl: CuratedCollection = {
  id: 'salad-bowl',
  name: 'Salad Bowl',
  emoji: '🥗',
  description: 'Grow everything for fresh, crisp salads. Greens, veggies, and edible flowers.',
  forExperience: 'all',
  forInterests: ['vegetables', 'herbs', 'flowers'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 15,
  tags: ['salad', 'greens', 'fresh', 'healthy'],
  filterFn: (plant) => {
    const saladPlants = ['lettuce', 'spinach', 'arugula', 'kale', 'chard', 'radish', 'carrot', 'cucumber', 'tomato', 'pepper'];
    const name = plant.name.toLowerCase();
    return saladPlants.some(sp => name.includes(sp)) || 
           (plant.category === 'flower' && !!plant.uses?.includes('edible'));
  },
};

// Pollinator Paradise
export const pollinatorParadise: CuratedCollection = {
  id: 'pollinator-paradise',
  name: 'Pollinator Paradise',
  emoji: '🦋',
  description: 'Beautiful flowers that attract bees, butterflies, and beneficial insects to your garden.',
  forExperience: 'all',
  forInterests: ['flowers'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 20,
  tags: ['bees', 'butterflies', 'beneficial', 'beautiful'],
  filterFn: (plant) => {
    const pollinatorPlants = [
      'sunflower', 'zinnia', 'cosmos', 'marigold', 'borage', 'lavender', 
      'echinacea', 'bee balm', 'milkweed', 'aster', 'goldenrod', 'salvia'
    ];
    const name = plant.name.toLowerCase();
    return plant.category === 'flower' && 
           (pollinatorPlants.some(p => name.includes(p)) || 
            !!plant.companions?.includes('pollinator') ||
            !!plant.uses?.includes('pollinator'));
  },
};

// Raised Bed Starter
export const raisedBedStarter: CuratedCollection = {
  id: 'raised-bed-starter',
  name: 'Raised Bed Starter',
  emoji: '🔲',
  description: 'Perfect mix for a 4x8 raised bed. High-yield vegetables that love the improved soil.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised'],
  forSpaces: ['small', 'medium'],
  plantCount: 16,
  tags: ['raised-bed', 'high-yield', 'organized'],
  plantIds: [
    'tomato-roma',
    'tomato-cherry',
    'pepper-bell',
    'cucumber-slicing',
    'lettuce-mix',
    'spinach-bloomsdale',
    'carrot-nantes',
    'radish-cherry-belle',
    'bean-bush-blue-lake',
    'zucchini-black-beauty',
    'kale-lacinato',
    'swiss-chard-bright-lights',
    'basil-sweet',
    'parsley-italian-flat',
    'cilantro-slow-bolt',
    'onion-green-bunching',
  ],
};

// Herb Garden Essentials
export const herbGarden: CuratedCollection = {
  id: 'herb-garden',
  name: 'Herb Garden Essentials',
  emoji: '🌿',
  description: 'The essential culinary herbs every kitchen garden needs. Fresh flavor at your fingertips.',
  forExperience: 'all',
  forInterests: ['herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['culinary', 'cooking', 'aromatic', 'kitchen'],
  filterFn: (plant) => {
    const essentialHerbs = [
      'basil', 'parsley', 'cilantro', 'dill', 'chives', 'thyme',
      'rosemary', 'oregano', 'sage', 'mint', 'tarragon', 'lavender'
    ];
    const name = plant.name.toLowerCase();
    return plant.category === 'herb' && 
           essentialHerbs.some(h => name.includes(h));
  },
};

// Tomato Lover's Collection
export const tomatoLovers: CuratedCollection = {
  id: 'tomato-lovers',
  name: "Tomato Lover's",
  emoji: '🍅',
  description: 'For the tomato obsessed! Cherry, beefsteak, paste, and heirloom varieties.',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['medium', 'large'],
  plantCount: 20,
  tags: ['tomatoes', 'heirloom', 'variety', 'obsession'],
  filterFn: (plant) => {
    const name = plant.name.toLowerCase();
    return name.includes('tomato') && plant.category === 'vegetable';
  },
};

// Heat Warriors - Hot climate plants
export const heatWarriors: CuratedCollection = {
  id: 'heat-warriors',
  name: 'Heat Warriors',
  emoji: '☀️',
  description: 'Plants that thrive in hot summers. Drought-tolerant and sun-loving varieties.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 18,
  tags: ['heat-tolerant', 'drought-resistant', 'summer', 'sun-loving'],
  filterFn: (plant) => {
    const heatPlants = [
      'okra', 'eggplant', 'pepper', 'tomato', 'sweet potato', 'cowpea',
      'melon', 'watermelon', 'squash', 'basil', 'rosemary', 'thyme',
      'lavender', 'sage', 'oregano'
    ];
    const name = plant.name.toLowerCase();
    return heatPlants.some(p => name.includes(p)) || 
           plant.water === 'low' ||
           !!plant.hardinessZones?.includes('10') ||
           !!plant.hardinessZones?.includes('11');
  },
};

// Shade Tolerant
export const shadeTolerant: CuratedCollection = {
  id: 'shade-tolerant',
  name: 'Shade Tolerant',
  emoji: '🌥️',
  description: 'Plants that produce even with limited sunlight. Perfect for partially shaded gardens.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs', 'flowers'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 15,
  tags: ['shade', 'partial-sun', 'low-light', 'forest-garden'],
  filterFn: (plant) => {
    return plant.sun === 'partial' || plant.sun === 'shade';
  },
};

// ==================== COOKING STYLE COLLECTIONS ====================

// Italian Garden
export const italianGarden: CuratedCollection = {
  id: 'italian-garden',
  name: 'Italian Garden',
  emoji: '🍝',
  description: 'Everything for authentic Italian cooking. Tomatoes, basil, oregano, and more for pasta night!',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['italian', 'pasta', 'pizza', 'mediterranean', 'cooking'],
  filterFn: (plant) => {
    const italianPlants = ['tomato', 'basil', 'oregano', 'rosemary', 'garlic', 'zucchini', 'pepper', 'parsley', 'thyme', 'eggplant'];
    const name = plant.name.toLowerCase();
    return italianPlants.some(p => name.includes(p)) && 
           (plant.category === 'vegetable' || plant.category === 'herb' || plant.category === 'allium');
  },
};

// Asian Stir-Fry Garden
export const asianStirFry: CuratedCollection = {
  id: 'asian-stir-fry',
  name: 'Asian Stir-Fry',
  emoji: '🍜',
  description: 'Fresh ingredients for delicious stir-fries. Bok choy, snap peas, ginger, and Thai basil.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 14,
  tags: ['asian', 'stir-fry', 'chinese', 'thai', 'cooking'],
  filterFn: (plant) => {
    const asianPlants = ['bok choy', 'napa cabbage', 'snap pea', 'snow pea', 'ginger', 'thai basil', 'scallion', 'green onion', 'daikon', 'mizuna', 'tatsoi', 'pac choi', 'gai lan', 'komatsuna'];
    const name = plant.name.toLowerCase();
    return asianPlants.some(p => name.includes(p)) ||
           (plant.category === 'vegetable' && (name.includes('chinese') || name.includes('asian')));
  },
};

// Mediterranean Garden
export const mediterraneanGarden: CuratedCollection = {
  id: 'mediterranean-garden',
  name: 'Mediterranean Garden',
  emoji: '🥙',
  description: 'Sun-loving classics from the Mediterranean. Perfect for fresh salads and grilled dishes.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 15,
  tags: ['mediterranean', 'greek', 'salad', 'grilling', 'healthy'],
  filterFn: (plant) => {
    const medPlants = ['tomato', 'cucumber', 'eggplant', 'pepper', 'oregano', 'thyme', 'parsley', 'rosemary', 'basil', 'garlic', 'onion', 'zucchini'];
    const name = plant.name.toLowerCase();
    return medPlants.some(p => name.includes(p)) &&
           (plant.category === 'vegetable' || plant.category === 'herb' || plant.category === 'allium');
  },
};

// Taco Night Garden
export const tacoNight: CuratedCollection = {
  id: 'taco-night',
  name: 'Taco Night Garden',
  emoji: '🌮',
  description: 'Everything for taco Tuesday! Cilantro, peppers, tomatoes, lettuce, and more.',
  forExperience: 'novice',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 10,
  tags: ['mexican', 'tacos', 'salsa', 'cilantro', 'party'],
  filterFn: (plant) => {
    const tacoPlants = ['cilantro', 'pepper', 'tomato', 'lettuce', 'onion', 'garlic', 'corn', 'lime', 'cabbage', 'radish'];
    const name = plant.name.toLowerCase();
    return tacoPlants.some(p => name.includes(p)) &&
           (plant.category === 'vegetable' || plant.category === 'herb' || plant.category === 'allium');
  },
};

// Indian Curry Garden
export const indianCurry: CuratedCollection = {
  id: 'indian-curry',
  name: 'Indian Curry Garden',
  emoji: '🍛',
  description: 'Aromatic spices and vegetables for authentic Indian curries and dishes.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['indian', 'curry', 'spicy', 'aromatic', 'cooking'],
  filterFn: (plant) => {
    const curryPlants = ['cilantro', 'turmeric', 'ginger', 'pepper', 'eggplant', 'tomato', 'spinach', 'okra', 'cumin', 'coriander', 'fenugreek', 'mustard'];
    const name = plant.name.toLowerCase();
    return curryPlants.some(p => name.includes(p)) &&
           (plant.category === 'vegetable' || plant.category === 'herb');
  },
};

// ==================== PURPOSE COLLECTIONS ====================

// Canning & Preserving Garden
export const canningGarden: CuratedCollection = {
  id: 'canning-garden',
  name: 'Canning & Preserving',
  emoji: '🥫',
  description: 'High-yield varieties perfect for canning, pickling, and preserving your harvest.',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['medium', 'large'],
  plantCount: 15,
  tags: ['canning', 'preserving', 'pickles', 'high-yield', 'storage'],
  filterFn: (plant) => {
    const canningPlants = ['tomato', 'cucumber', 'bean', 'pepper', 'beet', 'carrot', 'onion', 'corn', 'squash', 'berry'];
    const name = plant.name.toLowerCase();
    // Look for canning varieties (Roma, paste, pickling)
    const isCanningVariety = name.includes('roma') || name.includes('paste') || name.includes('pickling') || 
                            name.includes('canning') || name.includes('determinate');
    return canningPlants.some(p => name.includes(p)) && 
           (plant.category === 'vegetable' || plant.category === 'berry') &&
           (isCanningVariety || plant.daysToHarvest > 70); // Longer season = better for canning
  },
};

// Freezer Friendly Garden
export const freezerGarden: CuratedCollection = {
  id: 'freezer-garden',
  name: 'Freezer Friendly',
  emoji: '🧊',
  description: 'Plants that freeze well for year-round enjoyment. Stock your freezer!',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'fruits'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['medium', 'large'],
  plantCount: 14,
  tags: ['freezer', 'meal-prep', 'storage', 'convenience'],
  filterFn: (plant) => {
    const freezerPlants = ['broccoli', 'corn', 'bean', 'pea', 'spinach', 'kale', 'pepper', 'berry', 'squash', 'pumpkin'];
    const name = plant.name.toLowerCase();
    return freezerPlants.some(p => name.includes(p)) &&
           (plant.category === 'vegetable' || plant.category === 'berry');
  },
};

// Cut & Come Again Garden
export const cutAndComeAgain: CuratedCollection = {
  id: 'cut-and-come-again',
  name: 'Cut & Come Again',
  emoji: '🥗',
  description: 'Continuous harvest greens. Cut what you need and they keep growing!',
  forExperience: 'novice',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['continuous-harvest', 'greens', 'salad', 'leafy', 'fast'],
  filterFn: (plant) => {
    const cutPlants = ['lettuce', 'spinach', 'kale', 'chard', 'arugula', 'mesclun', 'mustard', 'collard', 'bok choy'];
    const name = plant.name.toLowerCase();
    return cutPlants.some(p => name.includes(p)) && plant.category === 'vegetable';
  },
};

// Cocktail Garden
export const cocktailGarden: CuratedCollection = {
  id: 'cocktail-garden',
  name: 'Cocktail Garden',
  emoji: '🍹',
  description: 'Fresh ingredients for craft cocktails. Mint, basil, cucumbers, and more!',
  forExperience: 'intermediate',
  forInterests: ['herbs', 'vegetables', 'fruits'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium'],
  plantCount: 12,
  tags: ['cocktails', 'drinks', 'entertaining', 'fresh', 'party'],
  filterFn: (plant) => {
    const cocktailPlants = ['mint', 'basil', 'cucumber', 'tomato', 'pepper', 'strawberry', 'blueberry', 'lemon', 'lime', 'lavender', 'rosemary', 'thyme'];
    const name = plant.name.toLowerCase();
    return cocktailPlants.some(p => name.includes(p));
  },
};

// Tea Garden
export const teaGarden: CuratedCollection = {
  id: 'tea-garden',
  name: 'Tea Garden',
  emoji: '🍵',
  description: 'Grow your own herbal teas. Chamomile, mint, lemon balm, and more soothing herbs.',
  forExperience: 'novice',
  forInterests: ['herbs', 'flowers'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['tea', 'herbal', 'relaxing', 'wellness', 'drying'],
  filterFn: (plant) => {
    const teaPlants = ['chamomile', 'mint', 'lemon balm', 'lavender', 'echinacea', 'hibiscus', 'rose', 'jasmine', 'lemongrass', 'bee balm', 'anise', 'fennel'];
    const name = plant.name.toLowerCase();
    return teaPlants.some(p => name.includes(p)) &&
           (plant.category === 'herb' || plant.category === 'flower');
  },
};

// ==================== GROWING CHALLENGE COLLECTIONS ====================

// 30-Day Challenge Garden
export const thirtyDayChallenge: CuratedCollection = {
  id: 'thirty-day-challenge',
  name: '30-Day Challenge',
  emoji: '⏱️',
  description: 'See results fast! Radishes, microgreens, and lettuce ready in 30 days or less.',
  forExperience: 'novice',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 10,
  tags: ['fast', 'quick-win', 'beginner-friendly', '30-days', 'rewarding'],
  filterFn: (plant) => {
    // Plants with harvest time <= 45 days
    return plant.daysToHarvest <= 45 && 
           (plant.category === 'vegetable' || plant.category === 'herb' || plant.category === 'microgreen');
  },
};

// Kids' Garden
export const kidsGarden: CuratedCollection = {
  id: 'kids-garden',
  name: "Kids' Garden",
  emoji: '🌻',
  description: 'Fun, easy plants kids love to grow. Big seeds, fast results, and tasty snacks!',
  forExperience: 'novice',
  forInterests: ['vegetables', 'flowers', 'fruits'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['kids', 'children', 'fun', 'educational', 'easy'],
  filterFn: (plant) => {
    const kidsPlants = ['sunflower', 'cherry tomato', 'pumpkin', 'carrot', 'snap pea', 'strawberry', 'radish', 'lettuce', 'bean', 'corn', 'gourd', 'nasturtium'];
    const name = plant.name.toLowerCase();
    return kidsPlants.some(p => name.includes(p)) &&
           (plant.daysToHarvest < 90); // Fast results for kids
  },
};

// High Yield Garden
export const highYieldGarden: CuratedCollection = {
  id: 'high-yield',
  name: 'High Yield Garden',
  emoji: '🏆',
  description: 'Maximum production per plant. Feed your family from a small space!',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['high-yield', 'productive', 'feed-family', 'efficient'],
  filterFn: (plant) => {
    const highYieldPlants = ['zucchini', 'tomato', 'cucumber', 'bean', 'pepper', 'squash', 'eggplant', 'okra', 'kale', 'chard'];
    const name = plant.name.toLowerCase();
    return highYieldPlants.some(p => name.includes(p)) && plant.category === 'vegetable';
  },
};

// Budget Saver Garden
export const budgetSaver: CuratedCollection = {
  id: 'budget-saver',
  name: 'Budget Saver Garden',
  emoji: '💰',
  description: 'Grow expensive store items for pennies. Herbs, salad greens, and specialty crops.',
  forExperience: 'novice',
  forInterests: ['vegetables', 'herbs', 'fruits'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 14,
  tags: ['budget', 'save-money', 'expensive-store-items', 'herbs', 'greens'],
  filterFn: (plant) => {
    // Herbs are expensive to buy
    if (plant.category === 'herb') return true;
    // Salad greens
    const name = plant.name.toLowerCase();
    const expensiveVeggies = ['tomato', 'pepper', 'berry', 'asparagus', 'artichoke', ' Brussels', 'heirloom'];
    return expensiveVeggies.some(p => name.includes(p));
  },
};

// ==================== SEASONAL COLLECTIONS ====================

// Spring Early Garden
export const springEarly: CuratedCollection = {
  id: 'spring-early',
  name: 'Spring Early Garden',
  emoji: '🌸',
  description: 'Cool-weather crops for early spring. Peas, lettuce, spinach, and radishes.',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 14,
  tags: ['spring', 'cool-weather', 'early-season', 'cold-hardy'],
  filterFn: (plant) => {
    const springPlants = ['pea', 'lettuce', 'spinach', 'radish', 'broccoli', 'cabbage', 'kale', 'chard', 'onion', 'shallot', 'asparagus', 'rhubarb'];
    const name = plant.name.toLowerCase();
    return springPlants.some(p => name.includes(p)) && 
           (plant.plantingSeason === 'spring' || !!plant.hardinessZones?.includes('3') || !!plant.hardinessZones?.includes('4'));
  },
};

// Summer Bounty Garden
export const summerBounty: CuratedCollection = {
  id: 'summer-bounty',
  name: 'Summer Bounty Garden',
  emoji: '☀️',
  description: 'Heat-loving summer staples. Tomatoes, peppers, cucumbers, and more!',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['medium', 'large'],
  plantCount: 16,
  tags: ['summer', 'heat-loving', 'main-crop', 'abundant'],
  filterFn: (plant) => {
    const summerPlants = ['tomato', 'pepper', 'cucumber', 'squash', 'bean', 'corn', 'eggplant', 'okra', 'melon', 'watermelon', 'basil', 'sweet potato'];
    const name = plant.name.toLowerCase();
    return summerPlants.some(p => name.includes(p)) && 
           (plant.category === 'vegetable' || plant.category === 'herb' || plant.category === 'cucurbit');
  },
};

// Fall Harvest Garden
export const fallHarvest: CuratedCollection = {
  id: 'fall-harvest',
  name: 'Fall Harvest Garden',
  emoji: '🍂',
  description: 'Cool-weather crops for autumn harvest. Pumpkins, Brussels sprouts, and root vegetables.',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['medium', 'large'],
  plantCount: 14,
  tags: ['fall', 'autumn', 'storage-crops', 'cool-weather'],
  filterFn: (plant) => {
    const fallPlants = ['pumpkin', 'squash', 'brussels sprout', 'kale', 'carrot', 'beet', 'turnip', 'rutabaga', 'parsnip', 'cabbage', 'cauliflower', 'broccoli'];
    const name = plant.name.toLowerCase();
    return fallPlants.some(p => name.includes(p)) && plant.category === 'vegetable';
  },
};

// Winter Hardy Garden
export const winterHardy: CuratedCollection = {
  id: 'winter-hardy',
  name: 'Winter Hardy Garden',
  emoji: '❄️',
  description: 'Cold-hardy crops that survive winter. Kale, spinach, garlic, and overwintering vegetables.',
  forExperience: 'expert',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 12,
  tags: ['winter', 'cold-hardy', 'overwinter', 'year-round', 'hardy'],
  filterFn: (plant) => {
    const winterPlants = ['kale', 'spinach', 'garlic', 'onion', 'leek', 'carrot', 'parsnip', 'brussels sprout', 'cabbage', 'collard', 'mache', 'claytonia'];
    const name = plant.name.toLowerCase();
    return winterPlants.some(p => name.includes(p)) &&
           (!!plant.hardinessZones?.includes('4') || !!plant.hardinessZones?.includes('5') || !!plant.hardinessZones?.includes('6'));
  },
};

// ==================== SPECIAL DIET COLLECTIONS ====================

// Keto Friendly Garden
export const ketoFriendly: CuratedCollection = {
  id: 'keto-friendly',
  name: 'Keto Friendly Garden',
  emoji: '🥑',
  description: 'Low-carb vegetables perfect for keto and low-carb diets. No starchy vegetables.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 16,
  tags: ['keto', 'low-carb', 'healthy', 'diet', 'weight-loss'],
  filterFn: (plant) => {
    // Avoid high-carb plants
    const highCarb = ['potato', 'sweet potato', 'corn', 'pea', 'bean', 'lentil', 'chickpea'];
    const name = plant.name.toLowerCase();
    if (highCarb.some(p => name.includes(p))) return false;
    // Include leafy greens, cruciferous, alliums, herbs
    const ketoPlants = ['lettuce', 'spinach', 'kale', 'chard', 'broccoli', 'cauliflower', 'cabbage', 'brussels sprout', 'zucchini', 'cucumber', 'pepper', 'tomato', 'eggplant', 'asparagus', 'celery', 'herb'];
    return ketoPlants.some(p => name.includes(p)) || plant.category === 'herb';
  },
};

// Gluten-Free Garden
export const glutenFree: CuratedCollection = {
  id: 'gluten-free',
  name: 'Gluten-Free Garden',
  emoji: '🌾',
  description: 'Naturally gluten-free vegetables and herbs. Safe for celiac and gluten-sensitive diets.',
  forExperience: 'all',
  forInterests: ['vegetables', 'herbs', 'fruits', 'flowers'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 20,
  tags: ['gluten-free', 'celiac-safe', 'allergen-free', 'healthy'],
  filterFn: (plant) => {
    // Most vegetables are naturally gluten-free
    // Exclude grains (wheat, barley, rye)
    return plant.category !== 'grain' && plant.category !== 'cover_crop';
  },
};

// Protein Power Garden
export const proteinPower: CuratedCollection = {
  id: 'protein-power',
  name: 'Protein Power Garden',
  emoji: '💪',
  description: 'High-protein vegetables and legumes. Great for plant-based diets and protein needs.',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'herbs'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['medium', 'large'],
  plantCount: 14,
  tags: ['protein', 'legumes', 'plant-based', 'healthy', 'fitness'],
  filterFn: (plant) => {
    const proteinPlants = ['bean', 'pea', 'lentil', 'chickpea', 'edamame', 'soybean', 'fava bean', 'spinach', 'broccoli', 'brussels sprout', 'asparagus', 'quinoa', 'amaranth'];
    const name = plant.name.toLowerCase();
    return proteinPlants.some(p => name.includes(p));
  },
};

// ==================== AESTHETIC/THEME COLLECTIONS ====================

// Rainbow Garden
export const rainbowGarden: CuratedCollection = {
  id: 'rainbow-garden',
  name: 'Rainbow Garden',
  emoji: '🌈',
  description: 'Colorful vegetables in every hue. Red tomatoes, orange carrots, purple cabbage, and more!',
  forExperience: 'intermediate',
  forInterests: ['vegetables', 'flowers', 'fruits'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 18,
  tags: ['colorful', 'rainbow', 'beautiful', 'fun', 'instagram-worthy'],
  filterFn: (plant) => {
    const colorfulPlants = ['tomato', 'carrot', 'pepper', 'cabbage', 'cauliflower', 'bean', 'corn', 'beet', 'chard', 'kale', 'radish', 'eggplant'];
    const name = plant.name.toLowerCase();
    // Look for colorful variety names
    const hasColor = ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'rainbow', 'golden', 'ruby', 'black'];
    return colorfulPlants.some(p => name.includes(p)) &&
           (hasColor.some(c => name.includes(c)) || plant.category === 'flower');
  },
};

// Butterfly Garden
export const butterflyGarden: CuratedCollection = {
  id: 'butterfly-garden',
  name: 'Butterfly Garden',
  emoji: '🦋',
  description: 'Host and nectar plants that attract butterflies. Milkweed, butterfly bush, and colorful flowers.',
  forExperience: 'intermediate',
  forInterests: ['flowers'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 14,
  tags: ['butterflies', 'pollinators', 'beautiful', 'educational', 'kids'],
  filterFn: (plant) => {
    const butterflyPlants = ['milkweed', 'butterfly bush', 'zinnia', 'coneflower', 'lantana', 'verbena', 'salvia', 'aster', 'goldenrod', 'phlox', 'bee balm', 'cosmos'];
    const name = plant.name.toLowerCase();
    return butterflyPlants.some(p => name.includes(p)) && plant.category === 'flower';
  },
};

// Bee Sanctuary Garden
export const beeSanctuary: CuratedCollection = {
  id: 'bee-sanctuary',
  name: 'Bee Sanctuary',
  emoji: '🐝',
  description: 'Feed the bees! Nectar-rich flowers that support honeybees and native pollinators.',
  forExperience: 'intermediate',
  forInterests: ['flowers', 'herbs'],
  forMethods: ['raised', 'ground', 'containers'],
  forSpaces: ['small', 'medium', 'large'],
  plantCount: 16,
  tags: ['bees', 'pollinators', 'honey', 'ecology', 'sustainable'],
  filterFn: (plant) => {
    const beePlants = ['lavender', 'borage', 'sunflower', 'cosmos', 'salvia', 'bee balm', 'echinacea', 'zinnia', 'basil', 'thyme', 'rosemary', 'oregano', 'mint', 'sage'];
    const name = plant.name.toLowerCase();
    return beePlants.some(p => name.includes(p)) &&
           (plant.category === 'flower' || plant.category === 'herb');
  },
};

// Three Sisters Garden
export const threeSisters: CuratedCollection = {
  id: 'three-sisters',
  name: 'Three Sisters Garden',
  emoji: '🏰',
  description: 'Traditional Native American companion planting. Corn, beans, and squash grow together in harmony.',
  forExperience: 'intermediate',
  forInterests: ['vegetables'],
  forMethods: ['raised', 'ground'],
  forSpaces: ['medium', 'large'],
  plantCount: 6,
  tags: ['companion-planting', 'native-american', 'traditional', 'sustainable', 'symbiotic'],
  plantIds: [
    'corn-sweet',           // Corn provides structure
    'bean-pole',            // Beans climb corn, fix nitrogen
    'squash-winter',        // Squash shades ground, prevents weeds
    'corn-dent',            // Alternative corn variety
    'bean-runner',          // Alternative bean variety
    'pumpkin-pie',          // Alternative squash variety
  ],
};

// All curated collections
export const allCollections: CuratedCollection[] = [
  // Beginner friendly
  beginnersDozen,
  thirtyDayChallenge,
  kidsGarden,
  budgetSaver,
  
  // By cooking style
  salsaGarden,
  italianGarden,
  asianStirFry,
  mediterraneanGarden,
  tacoNight,
  indianCurry,
  
  // By purpose
  saladBowl,
  herbGarden,
  canningGarden,
  freezerGarden,
  cutAndComeAgain,
  cocktailGarden,
  teaGarden,
  
  // By challenge/goal
  containerGarden,
  raisedBedStarter,
  highYieldGarden,
  
  // By season
  springEarly,
  summerBounty,
  fallHarvest,
  winterHardy,
  
  // By growing conditions
  tomatoLovers,
  heatWarriors,
  shadeTolerant,
  
  // Special diets
  ketoFriendly,
  glutenFree,
  proteinPower,
  
  // Aesthetic/theme
  rainbowGarden,
  butterflyGarden,
  beeSanctuary,
  pollinatorParadise,
  threeSisters,
];

// Helper function to get collections for a user
export function getCollectionsForUser(
  experience: string,
  interests: string[],
  method: string,
  space: string
): CuratedCollection[] {
  // If no preferences set, show all collections
  if (!experience || interests.length === 0) {
    return allCollections;
  }

  return allCollections.filter(collection => {
    // Match experience level - be more lenient
    // Novices see novice + intermediate collections
    // Intermediate sees all
    // Experts see all
    let expMatch = false;
    if (experience === 'novice') {
      expMatch = collection.forExperience === 'novice' || 
                 collection.forExperience === 'intermediate' ||
                 collection.forExperience === 'all';
    } else if (experience === 'intermediate' || experience === 'expert') {
      expMatch = true; // Intermediate and expert see everything
    } else {
      expMatch = collection.forExperience === 'all';
    }
    
    // Match at least one interest - be more inclusive
    // If user selected vegetables, show collections with vegetables
    // If user selected herbs, show collections with herbs
    // "surprise me" shows everything
    let interestMatch = false;
    if (interests.includes('surprise')) {
      interestMatch = true;
    } else {
      // Check if collection matches any selected interest
      interestMatch = collection.forInterests.some(i => interests.includes(i));
      
      // Also show beginner collections to everyone
      if (collection.forExperience === 'novice') {
        interestMatch = true;
      }
      
      // Salsa garden is popular - show if they selected vegetables OR herbs
      if (collection.id === 'salsa-garden' && 
          (interests.includes('vegetables') || interests.includes('herbs'))) {
        interestMatch = true;
      }
      
      // Salad bowl - show for vegetables
      if (collection.id === 'salad-bowl' && interests.includes('vegetables')) {
        interestMatch = true;
      }
      
      // Herb garden - show for herbs OR vegetables
      if (collection.id === 'herb-garden' && 
          (interests.includes('herbs') || interests.includes('vegetables'))) {
        interestMatch = true;
      }
    }
    
    // Match growing method - be lenient
    const methodMatch = collection.forMethods.includes(method) || 
                        collection.forMethods.includes('all') ||
                        method === 'unsure' ||
                        method === ''; // Empty method matches all
    
    // Match space - be lenient
    const spaceMatch = collection.forSpaces.includes(space) ||
                       collection.forSpaces.includes('small') || // Small gardens work for everyone
                       space === 'unsure' ||
                       space === ''; // Empty space matches all
    
    return expMatch && interestMatch && methodMatch && spaceMatch;
  });
}

// Get plants for a collection
export function getPlantsForCollection(
  collection: CuratedCollection,
  allPlants: PlantData[]
): PlantData[] {
  if (collection.plantIds) {
    // Use specific plant IDs
    return collection.plantIds
      .map(id => allPlants.find(p => p.id === id))
      .filter((p): p is PlantData => p !== undefined);
  } else if (collection.filterFn) {
    // Use filter function
    return allPlants.filter(collection.filterFn).slice(0, collection.plantCount);
  }
  return [];
}
